import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useCredentials from 'hooks/useCredentials';

// Holds the logged-in user's translation assignments so the sidebar badge, the
// notification bell, and the My Assignments screen all read from one source.
// Uses a guarded axios call directly (not useApiCaller) so it's safe to mount
// above the login route — it simply no-ops when the user isn't authenticated.
const AssignmentsContext = createContext({
  assignments: [],
  counts: { pending: 0, submitted: 0, approved: 0, rejected: 0 },
  actionNeeded: 0,
  loading: false,
  refresh: () => {},
});

export const useAssignments = () => useContext(AssignmentsContext);

// An assignment "needs action" when it's the translator's turn (pending or sent back)
// AND its task is still live — a cancelled task should never nag the translator.
export const needsAction = (a) =>
  (a?.status === 'pending' || a?.status === 'rejected') && a?.task?.status !== 'cancelled';

export const AssignmentsProvider = ({ children }) => {
  const { token, userData, isAuthenticated } = useCredentials();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !userData?.employeeId) {
      setAssignments([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/task/assigned`, {
        headers: { Authorization: `${token}`, userid: userData.employeeId },
      });
      setAssignments(res?.data?.data?.assignments ?? []);
    } catch (err) {
      // Silent: a user with no task role simply has no assignments; the badge/bell
      // just stay empty rather than throwing an error at people who don't translate.
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userData?.employeeId, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const counts = useMemo(() => {
    const c = { pending: 0, submitted: 0, approved: 0, rejected: 0 };
    assignments.forEach((a) => {
      if (c[a.status] != null) c[a.status] += 1;
    });
    return c;
  }, [assignments]);

  // exclude assignments whose task was cancelled — they no longer need action
  const actionNeeded = useMemo(() => assignments.filter(needsAction).length, [assignments]);

  const value = useMemo(
    () => ({ assignments, counts, actionNeeded, loading, refresh }),
    [assignments, counts, actionNeeded, loading, refresh]
  );

  return <AssignmentsContext.Provider value={value}>{children}</AssignmentsContext.Provider>;
};
